import { ComponentType } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EnvironmentInjector,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  WhitelabeledComponentKey,
  WhitelabeledComponentProps,
} from '../../../../whitelabel/whitelabel-component-types';
import { TooltipDirective } from '../../../osp-ui/components/overlay/tooltip/tooltip.directive';
import { WhitelabelManagerService } from '../../services/whitelabel-manager.service';

type PropsForKey<K extends WhitelabeledComponentKey> = WhitelabeledComponentProps[K];

@Component({
  selector: 'osp-whitelabeled-component',
  standalone: true,
  styleUrl: './whitelabeled-component.component.scss',
  template: environment.DEBUG
    ? '<div [className]="debugClass" [ospTooltip]="debug ? debugTooltip : \'\'"><ng-container #container></ng-container></div>'
    : '<ng-container #container></ng-container>',
  imports: [TooltipDirective],
})
export class WhitelabeledComponent<K extends WhitelabeledComponentKey> implements OnInit, OnChanges {
  @Input() componentKey!: K;
  @Input() defaultComponent?: ComponentType<unknown>;
  @Input() props!: PropsForKey<K>;

  @ViewChild('container', { read: ViewContainerRef, static: true }) container!: ViewContainerRef;

  public debug = environment.DEBUG;

  public componentRef?: ComponentRef<unknown>;
  private environmentInjector = inject(EnvironmentInjector);
  private whitelabelManagerService = inject(WhitelabelManagerService);

  protected debugTooltip: string = 'Whitelabeled Component ';
  protected debugClass: string = '';

  async ngOnInit() {
    await this.loadAndRenderComponent();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['componentKey'] && !changes['componentKey'].firstChange) {
      await this.loadAndRenderComponent();
    } else if (changes['props'] && this.componentRef && this.props) {
      this.applyProps(changes['props'].currentValue);
    }
  }

  private async loadAndRenderComponent() {
    this.container.clear();
    const config = this.whitelabelManagerService.getWhitelabelComponentConfiguration();

    const componentLoader = config[this.componentKey];
    const componentType = componentLoader ? await componentLoader() : this.defaultComponent;

    this.debugTooltip += `${this.componentKey}:\n`;

    if (componentLoader) {
      this.debugTooltip += 'loaded Component ' + componentType?.name;
      this.debugClass = 'debug-wrapper loaded-component';
    } else {
      this.debugTooltip += 'loaded Default Component ' + this.defaultComponent?.name;
      this.debugClass = 'debug-wrapper default-component';
    }

    if (!componentType) {
      console.error(`No component found for key "${this.componentKey}" and no default component provided!`);
      return;
    }

    this.componentRef = this.container.createComponent(componentType, {
      environmentInjector: this.environmentInjector,
    });

    // Ensure initial view render for dynamically created component (esp. OnPush)
    this.refreshView();

    if (this.props) this.applyProps(this.props);
  }

  private applyProps(props: Partial<Record<string, unknown>>) {
    if (!this.componentRef) return;
    const instance = this.componentRef.instance as Record<string, unknown>;
    Object.entries(props).forEach(([key, value]) => {
      console.log('Setting props on:', this.componentRef?.instance);
      this.componentRef?.setInput(key, value);
    });
    if (typeof instance['onPropsChanged'] === 'function') {
      instance['onPropsChanged']();
    }

    // Inputs updated after creation may need a manual CD tick
    this.refreshView();
  }

  private refreshView() {
    const cdr: ChangeDetectorRef | undefined = this.componentRef?.changeDetectorRef;
    if (cdr) {
      cdr.detectChanges();
    }
  }
}
